/**
 * ProviderIcons — monochrome brand marks for the AI providers we track.
 *
 * Each component is an inline SVG that renders in `currentColor`, so callers
 * style it like any other text glyph (`text-foreground`, `text-muted-foreground`,
 * `hover:text-primary`, etc.). No external dependency, no PNGs, no theming
 * surprises in dark mode.
 *
 * Brand-mark sources are public corporate identity assets, simplified into
 * single-color marks suitable for nav/landing/marquee use. The `local` entry
 * is intentionally a neutral chip icon since the "self-hosted" category spans
 * many vendors (Llama, Qwen, DeepSeek, Mistral-open, Phi, Gemma, etc.).
 */

import type { AIProvider } from '@aicostguard-dev/shared';

interface IconProps {
  className?: string;
  size?: number;
  title?: string;
}

const baseProps = (className?: string, size = 24, title?: string) => ({
  className,
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'currentColor',
  xmlns: 'http://www.w3.org/2000/svg',
  role: 'img' as const,
  'aria-label': title,
});

/** OpenAI — six-petal "knot" mark. */
export function OpenAIIcon({ className, size, title = 'OpenAI' }: IconProps) {
  return (
    <svg {...baseProps(className, size, title)}>
      <title>{title}</title>
      <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.91 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855L13.108 8.36 15.121 7.2a.076.076 0 0 1 .072 0l4.83 2.791a4.495 4.495 0 0 1-.671 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .033-.062L14.275 4.05a4.5 4.5 0 0 1 6.677 4.66zM8.31 12.852l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.706 5.46a.795.795 0 0 0-.393.681zm1.097-2.365 2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z" />
    </svg>
  );
}

/** Anthropic — the "A" three-bar mark. */
export function AnthropicIcon({ className, size, title = 'Anthropic' }: IconProps) {
  return (
    <svg {...baseProps(className, size, title)}>
      <title>{title}</title>
      <path d="M17.304 3.541h-3.672l6.696 16.918H24Zm-10.608 0L0 20.459h3.744l1.37-3.553h7.005l1.369 3.553h3.744L10.536 3.541Zm-.371 10.223L8.616 7.82l2.291 5.945Z" />
    </svg>
  );
}

/** Google Gemini — four-pointed sparkle star. */
export function GeminiIcon({ className, size, title = 'Google Gemini' }: IconProps) {
  return (
    <svg {...baseProps(className, size, title)}>
      <title>{title}</title>
      <path d="M12 0c0 6.627-5.373 12-12 12 6.627 0 12 5.373 12 12 0-6.627 5.373-12 12-12-6.627 0-12-5.373-12-12Z" />
    </svg>
  );
}

/** Cohere — circular "C" mark. */
export function CohereIcon({ className, size, title = 'Cohere' }: IconProps) {
  return (
    <svg {...baseProps(className, size, title)}>
      <title>{title}</title>
      <path d="M7.092 14.066c.476 0 1.43-.026 2.797-.587 1.593-.654 4.756-1.844 7.043-3.07 1.6-.858 2.298-1.984 2.298-3.501 0-2.124-1.728-3.846-3.86-3.846H7.044C3.156 3.062 0 6.21 0 10.092s3.155 7.03 7.046 7.03h.046zm2.451 3.347a3.488 3.488 0 0 1 1.94-3.13l3.95-1.953a4.018 4.018 0 0 1 5.81 3.604 4.022 4.022 0 0 1-2.234 3.602l-3.95 1.953a3.487 3.487 0 0 1-5.043-3.131v-.945zm-4.967.84A2.576 2.576 0 0 1 7.151 15.68a2.575 2.575 0 0 1 2.574 2.574v3.169A2.576 2.576 0 0 1 7.151 24a2.576 2.576 0 0 1-2.575-2.578v-3.169z" />
    </svg>
  );
}

/** Mistral — banded "M" mark. */
export function MistralIcon({ className, size, title = 'Mistral AI' }: IconProps) {
  return (
    <svg {...baseProps(className, size, title)}>
      <title>{title}</title>
      <path d="M3.428 3.4h3.429v3.428H3.428V3.4zm13.714 0h3.43v3.428h-3.43V3.4z" />
      <path d="M3.428 6.828h6.857v3.429H3.428V6.828zm10.286 0h6.858v3.429h-6.858V6.828z" />
      <path d="M3.428 10.257h17.144v3.429H3.428v-3.429z" />
      <path d="M3.428 13.686h3.429v3.428H3.428v-3.428zm6.858 0h3.428v3.428h-3.428v-3.428zm6.856 0h3.43v3.428h-3.43v-3.428z" />
      <path d="M0 17.114h10.286v3.429H0v-3.429zm13.714 0H24v3.429H13.714v-3.429z" />
    </svg>
  );
}

/** Meta — infinity-style mark used for Llama on the public marquee. */
export function MetaIcon({ className, size, title = 'Meta' }: IconProps) {
  return (
    <svg {...baseProps(className, size, title)}>
      <title>{title}</title>
      <path d="M6.897 4.498c-2.046 0-3.683 1.553-4.957 3.527C.196 10.747 0 14.182 0 14.682c0 1.93.78 4.82 3.103 4.82 1.722 0 2.962-.81 5.205-4.732.001 0 .934-1.65 1.575-2.787.226.367.467.766.722 1.198l1.07 1.8c2.081 3.482 3.24 4.521 5.347 4.521 2.262 0 3.503-1.84 3.503-4.85 0-3.156-.225-7.058-2.025-9.991-1.196-1.951-2.74-3.067-4.5-3.067-1.846 0-3.339 1.395-5.005 3.626-1.27-1.59-2.354-3.622-4.098-3.622zm-.064 2.583c.794 0 1.516.39 2.317 1.218-1.176 1.42-2.21 3.001-3.198 4.595-.832 1.346-1.45 2.21-1.93 2.21-.84 0-1.339-.748-1.339-2.144 0-3.131 1.563-5.879 4.15-5.879zm10.205 0c2.586 0 4.148 2.748 4.148 5.879 0 1.396-.498 2.144-1.338 2.144-.48 0-1.098-.864-1.93-2.21-.988-1.594-2.022-3.176-3.198-4.595.801-.828 1.524-1.218 2.318-1.218z" />
    </svg>
  );
}

/** Local / self-hosted — neutral chip mark. */
export function LocalIcon({ className, size, title = 'Self-hosted models' }: IconProps) {
  return (
    <svg {...baseProps(className, size, title)}>
      <title>{title}</title>
      <path d="M9 2v2h6V2h2v2h2a2 2 0 0 1 2 2v2h2v2h-2v4h2v2h-2v2a2 2 0 0 1-2 2h-2v2h-2v-2H9v2H7v-2H5a2 2 0 0 1-2-2v-2H1v-2h2V10H1V8h2V6a2 2 0 0 1 2-2h2V2h2zM5 6v12h14V6H5zm3 3h8v6H8V9z" />
    </svg>
  );
}

/**
 * Provider → icon component map. Use this anywhere we need the brand mark for
 * a known `AIProvider`. Falls back to LocalIcon for unknown / future entries.
 */
export const PROVIDER_BRAND_ICON: Record<AIProvider, React.FC<IconProps>> = {
  // Tier A — native brands
  openai: OpenAIIcon,
  anthropic: AnthropicIcon,
  gemini: GeminiIcon,
  cohere: CohereIcon,
  mistral: MistralIcon,
  // Tier B — OpenAI-compatible providers. For v0.2 we reuse the neutral
  // LocalIcon as a placeholder; brand-correct SVGs land in a follow-up so
  // the marquee doesn't get visually cluttered.
  xai: LocalIcon,
  groq: LocalIcon,
  together: LocalIcon,
  fireworks: LocalIcon,
  perplexity: LocalIcon,
  deepseek: LocalIcon,
  openrouter: LocalIcon,
  'vercel-ai-gateway': LocalIcon,
  // Self-hosted catch-all
  local: LocalIcon,
};

/**
 * Ordered list of providers as we surface them on the Landing logo marquee
 * and the Providers management page. Order is intentional: most-requested
 * vendors first; "local" last because it's a catch-all category.
 */
export const PROVIDER_DISPLAY_ORDER: AIProvider[] = [
  'openai',
  'anthropic',
  'gemini',
  'cohere',
  'mistral',
  'local',
];

/**
 * Extra logos used ONLY on the public landing marquee — they represent
 * popular *models* whose providers we already track (OpenAI's GPT, Meta's
 * Llama for the local category, etc.). Keeps the marquee visually richer
 * without changing our internal provider model.
 */
export const MARQUEE_EXTRAS: Array<{
  key: string;
  label: string;
  Icon: React.FC<IconProps>;
}> = [
  { key: 'meta', label: 'Meta · Llama', Icon: MetaIcon },
];
